import { useConfigStore } from '@/stores/ConfigStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useCardStore } from '@/stores/CardStore'
import { useBatchStore } from '@/stores/BatchStore'
import { useUserStore } from '@/stores/UserStore'
import { useInventoryStore } from '@/stores/InventoryStore'
import { isValidSku } from '@/helpers/sku'
import { isAllBatchesPageEnabled } from '@/helpers/featureFlags'
import { isRoundUpPriceEnabled } from '@/helpers/featureFlags'
import { 
  REQUIRED_FIELDS_FOR_LIST_TO_COLLX, 
  CONDITION, 
  TCG_CONDITION, 
  VIEW_GRADER_WEBSITE_LOOKUP_LINKS,
  EBAY_TYPES_BY_CATEGORIES,
  EBAY_MIN_PRICE_ERROR,
  MARKETPLACE_MIN_PRICE,
  MIN_PRICE_ERROR_BY_CONNECTION,
  minPriceError,
} from '@/constants'

/**
 * getBatchBasePath — the route prefix for the batch-details/inspector UI given
 * the current route. Returns '/inventory/batch' when the batch is open inside
 * the Inventory page (batch-in-inventory), else '/batches'. Lets the shared
 * Inspector/BatchDetails navigation build URLs that stay in the right section.
 */
export const getBatchBasePath = (route) =>
  route?.path?.startsWith('/inventory/batch') ? '/inventory/batch' : '/batches'

/**
 * Where "open this batch" goes — design-aware, unlike `getBatchBasePath` above,
 * which answers "where am I now?".
 *
 * The new design has no standalone batch page: a batch is the Inventory grid
 * filtered to `batch_id`, at `/inventory/batch/:id`. So every link that opens a
 * batch from OUTSIDE the batch UI — global search, the To-Dos list, "View in
 * batch" on an item, the All Batches list, the grid's Batch ID cell — has to
 * branch, and they should all branch the same way. One helper instead of a
 * `userStore.isNewDesign ? … : …` at each call site.
 *
 * Pass `cardId` for the deep links that open a specific card's inspector.
 */
export const batchViewPath = (batchId, { cardId = null, inspector = false } = {}) => {
  const base = useUserStore().isNewDesign ? '/inventory/batch' : '/batches'

  if (!batchId) return batchesListPath()

  // `inspector` without a card: land on the inspector and let it pick the
  // default card (the add-cards flows return here after an upload).
  if (cardId) return `${base}/${batchId}/inspector/${cardId}`
  if (inspector) return `${base}/${batchId}/inspector`

  return `${base}/${batchId}`
}

/**
 * Where "all my batches" lives — design-aware, like `batchViewPath`.
 *
 * The new design keeps batches inside Inventory: `/inventory/batches` is the
 * same list as legacy `/batches`, with Inventory's chrome and a row that opens
 * the batch at `/inventory/batch/:id`. Any redirect that lands a new-design user
 * on `/batches` drops them into legacy chrome they didn't ask for, so every such
 * destination routes through here.
 *
 * It used to answer `/inventory` for the new design, because there was no
 * batches page to send them to — only the All Batches panel. Now that the page
 * exists, "take me to my batches" lands on the batches.
 *
 * Gated on `isAllBatchesPageEnabled()` rather than `isNewDesign` while the page
 * is in trial: everyone else — legacy AND the rest of the Inventory beta — keeps
 * going to `/batches`. Routing them to a page the guard bounces would show a
 * "All Batches" link that snaps straight back.
 */
export const batchesListPath = () =>
  (isAllBatchesPageEnabled() ? '/inventory/batches' : '/batches')

/**
 * Load the cards for the open batch.
 *
 * Batch-in-inventory (new design) reads them from the INVENTORY endpoint filtered
 * by `batch_id` — literally the Inventory list request with one more param:
 *   /inventory/:inventoryId?page=1&perPage=25&sortBy=created_at.desc&batch_id=123
 * One endpoint for both inventory surfaces, so rows, filters and
 * `available_filters` are identical to the Inventory grid's.
 *
 * The classic `/batches/:id` page keeps `GET /batches/:id/cards`.
 */
export const isBatchInInventoryRoute = (route) => !!route?.path?.startsWith('/inventory/batch')

/**
 * Which batch this surface is scoped to — from the ROUTE, which is the only
 * thing that can't be stale.
 *
 * `batchStore.selectedBatchId` is a fallback, not the source: it is written by
 * whichever surface opened the batch, so a fetch racing that assignment used to
 * read `null` and send `batch_id=null`, which the backend ignores — returning
 * the dealer's ENTIRE inventory inside a batch view.
 */
export const routeBatchId = (route) => {
  const fromRoute = Number.parseInt(route?.params?.batchId, 10)

  if (Number.isFinite(fromRoute)) return fromRoute

  const fromStore = Number.parseInt(useBatchStore().selectedBatchId, 10)

  return Number.isFinite(fromStore) ? fromStore : null
}

/**
 * Re-apply the batch scope to a set of filters.
 *
 * MUST be used anywhere `cardStore.itemFilters` is rebuilt on a batch surface.
 * `itemFilters` is what every later `fetchInventory` reads — including the ones
 * Pusher fires on card updates and import progress — so a rebuild that drops
 * `batch_id` doesn't just lose it for one request: the next background refetch
 * silently replaces the batch's cards with the whole inventory, and it stays
 * wrong until a reload puts the scope back. That is exactly what dealers hit.
 *
 * The batch lives in the PATH (`/inventory/batch/:batchId`), never in
 * `route.query`, which is why assigning `route.query` straight onto
 * `itemFilters` stripped it.
 */
export const withBatchScope = (route, filters = {}) => {
  if (!isBatchInInventoryRoute(route)) return { ...filters }

  const id = routeBatchId(route)

  return id ? { ...filters, batch_id: String(id) } : { ...filters }
}

export const fetchCardsForBatch = async (route, query = {}) => {
  const cardStore = useCardStore()

  if (!isBatchInInventoryRoute(route)) {
    return cardStore.fetchSelectedCards(Object.keys(query).length ? query : null)
  }

  // `batch_id` is a first-class inventory param — it rides alongside the normal
  // filters/sort/search rather than folding into `filter=`.
  const userStore = useUserStore()
  const inventoryStore = useInventoryStore()

  cardStore.itemFilters = withBatchScope(route, query)

  return inventoryStore.fetchInventory({ id: userStore.user.default_inventory_id })
}

/**
 * Is this the Inventory GRID (`/inventory`, `/inventory/views/:id`) rather than a
 * batch surface? The Inspector panel opens there too (new design), but Inventory
 * has no `/:batchId/:cardId` route to drive — `/inventory/:cardId` is the full
 * details PAGE, so pushing it would unmount the grid behind the panel. Callers
 * use this to keep the panel pure UI state there: no URL, no navigation.
 */
export const isInventoryGridRoute = (route) =>
  !!route?.path?.startsWith('/inventory') && !route.path.startsWith('/inventory/batch')

/**
 * The bare GRID behind a panel URL — `/batches/12/345?tab=details` → `/batches/12`.
 *
 * Batch surfaces drive the Inspector panel from the URL (the card segment +
 * `?tab=`), so returning to such a path re-opens the panel. Closing the item
 * view outright has to land on the grid itself. Plain grid paths pass through
 * untouched, query and all — there the query is the user's filters, not panel
 * state, and dropping it would silently reset their view.
 */
export const gridPathWithoutPanel = (fullPath) => {
  const raw = String(fullPath || '')
  const [path] = raw.split('?')
  const panelPath = path.match(/^(\/batches\/\d+|\/inventory\/batch\/\d+)\/\d+$/)

  return panelPath ? panelPath[1] : raw
}

export const getDefaultSelectedCardId = () => {
  const cardStore = useCardStore()
  
  return cardStore.batchCardsBasedOnStatus.draftBatchCards.length ? cardStore.batchCardsBasedOnStatus.draftBatchCards[0].id : cardStore.batchCardsBasedOnStatus.readyToListBatchCards.length ? cardStore.batchCardsBasedOnStatus.readyToListBatchCards[0].id : null
}

export const getNextCardId = () => {
  const cardStore = useCardStore()
  const { draftBatchCards, readyToListBatchCards } = cardStore.batchCardsBasedOnStatus
  const currentId = cardStore.selectedCardId

  const currentIndex = draftBatchCards.findIndex(card => card.id === currentId)

  // The current card is still a draft — e.g. "Mark as Ready to List", which
  // fires an async status update but navigates synchronously, so the card
  // hasn't left the draft list yet. Advance to the *next* draft after it.
  if (currentIndex !== -1) {
    const nextDraft = draftBatchCards[currentIndex + 1]

    if (nextDraft) return nextDraft.id

    // Last in the draft list, but earlier drafts still remain (they'll stay
    // draft after this card flips). Wrap to the first of those remaining drafts
    // — skip `draftBatchCards[0]` when it *is* the current card, otherwise we'd
    // land back on the same card after it becomes Ready to List.
    const remainingDraft = draftBatchCards.find(card => card.id !== currentId)

    if (remainingDraft) return remainingDraft.id

    // No drafts left after this one → move into Ready to List. Fall back to
    // staying on the current card when nothing else is ready yet.
    return readyToListBatchCards[0]?.id || currentId
  }

  // The current card is no longer a draft (e.g. it was just deleted, or already
  // ready): fall back to the first remaining draft, then the first ready card.
  return draftBatchCards[0]?.id || readyToListBatchCards[0]?.id
}

export const omitQueryFilters = (fieldNames) => {
  const cardStore = useCardStore()
  
  return Object.keys(cardStore.itemFilters).reduce((acc, item) => {
    if (!fieldNames.includes(item)) {
      acc[item] = cardStore.itemFilters[item]
    }

    return acc
  }, {})
}

export const toggleAllRows = ({ params }) => {
  const cardStore = useCardStore()

  const haveUnselectedItems = !!cardStore.cards.find(element => element.select === false)

  if (haveUnselectedItems) {
    params.api.refreshCells({
      force: true,
      suppressFlash: true,
    })
    cardStore.cards = cardStore.cards.map((element, index) => {
      if (!element.select) {
        element.select = true
        if (cardStore.selectedRanges) {
          cardStore.selectedRanges.push({ 
            startIndex: index, 
            endIndex: index, 
            id: element.id,
            statusDisabled: element.status === 'draft' && element.has_errors,
            graded: element.graded 
          })

          cardStore.selectedCards[element.id] = element
        } else {
          cardStore.selectedRanges = [{ 
            startIndex: index, 
            endIndex: index, 
            id: element.id,
            statusDisabled: element.status === 'draft' && element.has_errors,
            graded: element.graded 
          }] 

          cardStore.selectedCards[element.id] = element    
        }
      }
      return element
    })
  } else {
    params.api.refreshCells({
      force: true,
      suppressFlash: true,
    })

    cardStore.cards.forEach(element => {
      if (element.select) {
        element.select = false
        cardStore.selectedRanges = null
        cardStore.selectedCards = {}
        cardStore.isBulkEditing = false
      }
    })
  }
}

export const handleOnRowClicked = (params, setFocus = false) => {
  const cardStore = useCardStore()
  
  cardStore.pinnedCardData.isMatchOnly = false
  cardStore.selectCard(params.data)

  if (params.data.select) {
    params.node.setSelected(true)
  }

  if (setFocus) {
    const selectedCell = document.querySelectorAll('.ag-cell-range-selected')

    if (selectedCell[0]) {
      selectedCell[0].classList.remove('ag-cell-range-selected')
      selectedCell[0].classList.remove('ag-cell-range-selected-1')
    }
    params.api.setFocusedCell(params.rowIndex, 'database_match')
  }
}

export const sortSubsetSuggestions = (a, b) => {
  const subsetA = a.set.subset !== null ? a.set.subset : a.set.name
  const subsetB = b.set.subset !== null ? b.set.subset : b.set.name

  if (subsetA < subsetB) return -1
  if (subsetA > subsetB) return 1
  return 0
}

export const getCardFiltersString = (card) => {
  const { graded, grader, grade_number, serial_number } = card

  let filters = `&_sop=15&Graded=${graded ? 'Yes' : 'No'}`

  if (grader) {
    filters += `&Grader=${grader}`
  }

  if (grade_number) {
    filters += `&Grade=${grade_number}`
  }

  if (serial_number) {
    filters += `&Features=Serial Numbered`
  }

  return filters
}

export const getCardNameForPricing = (card) => {
  let name = ''
  const { card_number, set, player, subset, variation, grader, grade_number, serial_number } = card

  if (set) {
    name = `${name} ${set}`
  } 

  if (card_number) {
    name = `${name} #${card_number}`
  }

  if (player) {
    name = `${name} ${player}`
  } 

  if (subset) {
    name = `${name} ${subset}`
  } 

  if (variation) {
    name = `${name} ${variation}`
  } 

  if (grader) {
    name = `${name} ${grader}`
  } 

  if (grade_number) {
    name = `${name} ${grade_number}`
  } 

  if (serial_number) {
    name = `${name} ${serial_number}`
  }

  return encodeURIComponent(name)
}

const EBAY_AFFILIATE_PARAMS = '&mkcid=1&mkrid=711-53200-19255-0&siteid=0&campid=5338833009&customid=&toolid=10001&mkevt=1'
export const ebayRecentSalesUrl = (card) =>
  `https://www.ebay.com/sch/i.html?LH_Sold=1&_nkw=${getCardNameForPricing(card)}${EBAY_AFFILIATE_PARAMS}`
export const ebayActiveListingsUrl = (card, { filters = false, sort = false } = {}) =>
  `https://www.ebay.com/sch/i.html?_nkw=${getCardNameForPricing(card)}${EBAY_AFFILIATE_PARAMS}${filters ? getCardFiltersString(card) : ''}${sort ? '&_sop=15' : ''}`

const addDigitsToSku = (skuNumber, skuWidth) => {
  let skuString = String(skuNumber)
  while (skuString.length < skuWidth) {
    skuString = '0' + skuString
  }
  return skuString
}

// Preview of the next auto-generated SKU. The prefix is concatenated with no
// separator because that is what the API actually emits (`ABC` + `10000000376`
// → `ABC10000000376`); the space this used to insert made the preview both
// wrong and — since CDP-2444 — a display of an illegal SKU character.
export const preparePreviewSku = (skuNumber, skuWidth, skuPrefix) => {
  const skuString = addDigitsToSku(skuNumber, skuWidth)
  return skuPrefix ? `${skuPrefix}${skuString}` : skuString
}

export const getTitle = (card) => {
  const selectedCard = card

  if (selectedCard?.title) {
    return selectedCard.title
  }

  if (selectedCard?.template_title) {
    return selectedCard.template_title
  }

  return ''
}

export const getSuggestedTitle = (card) =>
  card?.llm
    ? [card.llm.cardNumber, card.llm.name, card.llm.set, card.llm.serialNumber].filter(Boolean).join(' ').trim()
    : ''

export const getTitleUsingSpecificFields = (card, connection) => {
	if (!card.card_number && !card.player && !card.team) {
 		return card[`${connection}_title`]
	}

	const combinedTitle = `${card.card_number ? `#${card.card_number}` : ''} ${card.player || ''} ${card.team || ''}`
	
	return combinedTitle 
}

export const isItNoMatchFound = (card) => {
  const selectedCard = card

  if (!selectedCard?.title && !selectedCard.player && !selectedCard.set && !selectedCard.card_number && !selectedCard.team && !selectedCard.year) {
    return true
  }

  return false
}

export const getErrorsForListToCollX = (card, onlyErrors = false) => {
  const { errors, suggested_edits: suggestedEdits, type } = card
  const errorsList = []

  REQUIRED_FIELDS_FOR_LIST_TO_COLLX.forEach((field) => {
    if (field === 'category') {
      if (type === 'sports') {
        errors?.sport && errorsList.push(errors.sport)
        !onlyErrors && suggestedEdits?.sport && errorsList.push(suggestedEdits.sport)
      } else {
        errors?.game && errorsList.push(errors.game)
        !onlyErrors && suggestedEdits?.game && errorsList.push(suggestedEdits.game)
      }
    } else {
      if (errors && (field !== 'start_price' || errors[field] !== EBAY_MIN_PRICE_ERROR)) {
        errors[field] && errorsList.push(errors[field])
        !onlyErrors && suggestedEdits[field] && errorsList.push(suggestedEdits[field])
      }
    }
  })

  const cardStore = useCardStore()
  const configStore = useConfigStore()

  const graders = configStore.getConfigField('graders') || []

  if (card.graded && !card.cert_no) {
    errorsList.push('Cert No: To list on CollX, a certificate number is required for graded cards')
  }

  if (card.graded && card.grader) {
    // check if grader from CDP grader list
    const grader = graders.find((item) => item.name === card.grader)

    if (!grader) {
      errorsList.push('Grader: To list on CollX, please select a Grader from available options')
    }

    // check if grade number from CDP grade numbers list
    if (grader && card.grade_number) {
      const gradeNumber = grader.grading_scale.find((item) => item === card.grade_number)

      if (!gradeNumber) {
        errorsList.push('Grade: To list on CollX, please select a Grade from available options')
      }
    }

    if (grader && !card.grade_number) {
      errorsList.push('Grade: To list on CollX, please select a Grade from available options')
    }
  }

  if (card.graded && !card.grader) {
    errorsList.push('Grader: To list on CollX, please select a Grader from available options')
  }

  if (!card.graded && card.condition) {
    if (cardStore.selectedCardData.type === 'tcg' && !TCG_CONDITION.find(item => item.name === card.condition)) {
      errorsList.push('Condition: To list on CollX, please select Condition from available options')
    }

    if (cardStore.selectedCardData.type === 'sports' && !CONDITION.find(item => item.name === card.condition)) {
      errorsList.push('Condition: To list on CollX, please select Condition from available options')
    }
  }

  if (!card.graded && !card.condition) {
    errorsList.push('Condition: To list on CollX, please select Condition from available options')
  }

  return errorsList
}

export const preparedListOfCardsToExportOrPublish = ({ items, selectedCards = {}, isBulkEditing, selectedRanges = [] }) => {
  let items_ = [...items]

  if (isBulkEditing) {
    if (Object.keys(selectedCards).length && !selectedRanges.length) {
      items_ = Object.entries(selectedCards).map(([l, value]) => value)
    } else if (selectedRanges && selectedRanges.length) {
      items_ = selectedRanges.reduce((acc, item) => {
        const cardData = items_.find(card => card.id === item.id)

        if (cardData) acc.push(cardData)

        return acc
      }, [])
    }
  }

  const cardsIds = items_.reduce((acc, item) => {
    acc.push(item.id)

    return acc
  }, [])

  return { cardsIds, cards: items_ }
}

export const dateScheduledTemplateErrors = (connectionStore) => {
  const errors = []
  let dateScheduledTemplateError = false

  if (connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.schedule_date) {
    const scheduleDate = connectionStore.selectedTemplatePerConnection['ebay-direct'].schedule_date
    const scheduleTime = connectionStore.selectedTemplatePerConnection['ebay-direct'].schedule_time
    
    // Combine date and time
    const scheduledDateTime = new Date(`${scheduleDate} ${scheduleTime}`)
    const now = new Date()
    
    // Check if scheduled date/time is in the past
    if (scheduledDateTime <= now && !connectionStore.selectedTemplatePerConnection['ebay-direct'].make_live_immediately) {
      errors.push('Please update your template to use a future start date, or set the listing to go live immediately.')
      dateScheduledTemplateError = true
    }
  }

  return { errors, dateScheduledTemplateError }
}

export const hasEbayPoliciesTemplateErrors = (connectionStore) => {
  return !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_location_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_fulfillment_policy_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_payment_policy_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_return_policy_id
}

/**
 * The marketplace price floors — eBay's $0.99 and Whatnot's $1 — in one place.
 *
 * Everything below answers one question: does THIS card, going to THIS
 * marketplace, break THAT marketplace's floor? It is the only price rule the
 * app owns, and every surface that shows listing errors reads it, so a card
 * can't be blocked in the grid and allowed in the inspector.
 *
 * Why the app owns it at all, when the backend already validates prices: the
 * backend has ONE price error and it names eBay. Whatnot's floor is higher
 * ($1 vs $0.99) and was never checked, so a card at $0.50 sailed past every
 * screen in the app and was rejected by Whatnot instead. And "is this a
 * problem?" now depends on a per-template setting the backend error can't know
 * about — round-up is answered once per marketplace, not once per card.
 */

/** The listing template a connection actually lists with. */
const listingTemplateFor = (connectionKey, connectionStore) => {
  const templates = connectionStore?.selectedTemplatePerConnection || {}

  // eBay has two: `ebay` is the export template, `ebay-direct` the listing one,
  // and `marketplace` is what the error modal calls the same connection.
  if (connectionKey === 'ebay' || connectionKey === 'marketplace') {
    return templates[useCardStore().isTryToExport ? 'ebay' : 'ebay-direct']
  }

  return templates[connectionKey]
}

/**
 * Is this marketplace's template set to lift sub-floor prices on send?
 *
 * Per marketplace, deliberately: eBay's toggle used to forgive the floor on
 * Whatnot and Shopify too, which let a $0.50 card through to a Whatnot listing
 * that Whatnot then refused. One template's setting only ever speaks for its
 * own marketplace.
 *
 * Pilot-gated like the toggle itself — a template carrying the flag (saved by a
 * pilot dealer, or set server-side) must not change what Publish does for a
 * dealer who cannot see the control to turn it back off.
 */
export const roundsUpSalePrice = (connectionKey, connectionStore) => {
  if (!isRoundUpPriceEnabled()) return false

  return !!listingTemplateFor(connectionKey, connectionStore)?.round_up_sale_price_to_dollar
}

/**
 * The floor error this card earns on this marketplace, or '' when it earns none.
 *
 * Reads the card's SALE PRICE — the same field the backend judges for eBay — so
 * both marketplaces answer the question the same way, whatever the template's
 * Listing Price is set to.
 *
 * A missing or zero price is NOT this rule's business: that card is already
 * carrying "Sale Price is required" from the backend, and adding "must be at
 * least $1" on top names a second problem the dealer doesn't have.
 */
export const listingMinPriceError = (card, connectionKey, connectionStore) => {
  const key = connectionKey === 'marketplace' ? 'ebay' : connectionKey
  const floor = MARKETPLACE_MIN_PRICE[key]

  if (!floor) return ''

  // Rounding up answers the question before the price does: the marketplace is
  // sent the floor, so there is nothing to flag.
  if (roundsUpSalePrice(key, connectionStore)) return ''

  const price = Number(card?.start_price)
  const belowFloor = Number.isFinite(price) && price > 0 && price < floor

  // eBay keeps deferring to the BACKEND's verdict as well as ours. The two can
  // disagree — a template can price a listing from something other than the
  // sale price — and dropping a floor error the backend raised would unblock a
  // card eBay then rejects. Whatnot has no backend verdict to defer to, which
  // is why this rule exists at all.
  if (!belowFloor && !(key === 'ebay' && card?.errors?.start_price === EBAY_MIN_PRICE_ERROR)) return ''

  // The message names the round-up escape hatch only for dealers who have the
  // toggle — for everyone else the only fix is the price, and naming a control
  // they can't find reads as a bug in the app.
  return minPriceError(key, isRoundUpPriceEnabled())
}

/**
 * Is this string one of the floor errors, with or without the round-up hint?
 *
 * For surfaces that shorten `"Field: explanation"` to `"Field is required"`.
 * That reading is wrong here twice over: the price is set, so nothing is
 * "required", and the half being dropped is the half that says what to do
 * about it.
 */
export const isMinPriceError = (message) =>
  Object.values(MIN_PRICE_ERROR_BY_CONNECTION).some((base) => String(message ?? '').startsWith(base))

/**
 * Is the backend's eBay floor error the ONLY thing wrong with this card?
 *
 * Kept narrow on purpose. A card that is also missing a category is still
 * broken, and treating one recognised error as "nothing else is wrong" would
 * let it through to the marketplace to fail there instead. An unreadable
 * `errors` shape answers false, which keeps the card blocked.
 */
const isOnlyMinPriceError = (card) => {
  const errors = card?.errors

  if (!errors || typeof errors !== 'object') return false

  const fields = Object.keys(errors).filter((key) => errors[key])

  return fields.length === 1 && fields[0] === 'start_price' && errors.start_price === EBAY_MIN_PRICE_ERROR
}

/**
 * The errors to SHOW for one connection: the card's backend errors with the
 * eBay floor string stripped out, plus this marketplace's own floor error when
 * it applies.
 *
 * The strip is what makes the lists honest. That one string is eBay's rule, and
 * surfaces used to either show it verbatim on Whatnot, regex `ebay` out of it,
 * or drop it and show nothing — three answers to the same question. Now every
 * list asks this helper and eBay gets its sentence back through the same door
 * Whatnot gets its own.
 */
export const listingErrorsForConnection = (card, connectionKey, connectionStore) => {
  const raw = card?.errors ? Object.keys(card.errors).map((key) => card.errors[key]) : []
  const errors = raw.filter((error) => error !== EBAY_MIN_PRICE_ERROR)
  const minPrice = listingMinPriceError(card, connectionKey, connectionStore)

  return minPrice ? [minPrice, ...errors] : errors
}

export const validateIsCardsHasCategoryNotEqualToTemplate = (card, connectionStore) => {
  const cardStore = useCardStore()
  let isCardsWithoutCategoryBasedOnTemplate = false
  let isCardsHasCategoryNotEqualToTemplate = false

  if (card.type !== EBAY_TYPES_BY_CATEGORIES[connectionStore.selectedTemplatePerConnection?.[cardStore.isTryToExport ? 'ebay' : 'ebay-direct']?.ebay_category]) {
    isCardsHasCategoryNotEqualToTemplate = true
  }

  if (isCardsHasCategoryNotEqualToTemplate && card.type === EBAY_TYPES_BY_CATEGORIES[connectionStore.selectedTemplatePerConnection?.[cardStore.isTryToExport ? 'ebay' : 'ebay-direct']?.ebay_category]) {
    isCardsHasCategoryNotEqualToTemplate = false
    isCardsWithoutCategoryBasedOnTemplate = true
  }

  return { isCardsWithoutCategoryBasedOnTemplate, isCardsHasCategoryNotEqualToTemplate }
}

export const checkAreRequiredFieldsEmpty = (cards, connections) => {
  if (!connections.length) return

  const connectionStore = useConnectionStore()

  let isCollxError = false
  let isEbayError = false
  let isWhatnotError = false
  let isEbayTemplateError = false
  let isShopifyError = false
  let isDateScheduledTemplateError = false
  let cardsWithoutCategoryBasedOnTemplate = false
  let cardsHasCategoryNotEqualToTemplate = false

  /**
   * Validate the cards the CALLER handed us — nothing else.
   *
   * This used to substitute `cardStore.selectedCards` whenever a selection
   * existed, which quietly broke every per-card gate: the Inspector's "Mark as
   * Ready", the grid's status control and the listings cell all pass exactly
   * one card, and after a bulk edit (which leaves the selection in place) they
   * were validating the whole selection instead. One unrelated card missing a
   * required field disabled the button for the card you were actually looking
   * at — the "can't mark as ready after bulk editing Sales Price" report.
   *
   * The selection-wide callers are unaffected: `PublishCardsMenu`,
   * `ListToChannels` and `PublishingBlock` already resolve the selection
   * themselves through `preparedListOfCardsToExportOrPublish` and pass the
   * resulting list in.
   */
  const selectedCardsValues = cards

  const isNotListable = (card) =>
    (typeof card.quantity === 'number' && card.quantity <= 0) ||
    card.status === 'sold_out' ||
    card.inventoryStatus === 'sold_out'

  const zeroQtyCards = selectedCardsValues.filter(isNotListable)
  const zeroQuantityAll = zeroQtyCards.length === selectedCardsValues.length && selectedCardsValues.length > 0
  const zeroQuantityPartial = !zeroQuantityAll && zeroQtyCards.length > 0

  // any selected item whose SKU leaves the eBay-safe set is a blocking
  // error, handled like the other listing errors: it disables the Publish/List
  // button and the per-item reason is surfaced in the error modal.
  const skuInvalid = selectedCardsValues.some(card => !isValidSku(card.sku))

  for(let i = 0; i < cards.length; i++) {
    const errors = getErrorsForListToCollX(cards[i], true)

    if (!isEbayTemplateError) {
      isEbayTemplateError = hasEbayPoliciesTemplateErrors(connectionStore)
    }

    if (errors.length) {
      isCollxError = true
    }

    // A price floor blocks the ONE marketplace that enforces it — never all
    // three. The backend's price error names eBay, so it is taken out of the
    // blanket check and each marketplace is then asked about its own floor:
    // eBay at $0.99, Whatnot at $1, CollX and Shopify not at all. Any OTHER
    // error still blocks all three, unchanged.
    if (cards[i].has_errors && !isOnlyMinPriceError(cards[i])) {
      isEbayError = true
      isWhatnotError = true
      isShopifyError = true
    }

    if (listingMinPriceError(cards[i], 'ebay', connectionStore)) {
      isEbayError = true
    }

    if (listingMinPriceError(cards[i], 'whatnot', connectionStore)) {
      isWhatnotError = true
    }

    const { dateScheduledTemplateError } = dateScheduledTemplateErrors(connectionStore)
    const { isCardsWithoutCategoryBasedOnTemplate, isCardsHasCategoryNotEqualToTemplate } = validateIsCardsHasCategoryNotEqualToTemplate(cards[i], connectionStore)

    if (isCardsWithoutCategoryBasedOnTemplate) {
      cardsWithoutCategoryBasedOnTemplate = true
    }

    if (isCardsHasCategoryNotEqualToTemplate) {
      cardsHasCategoryNotEqualToTemplate = true
    }

    if (dateScheduledTemplateError) {
      isDateScheduledTemplateError = true
    }

    // Whatnot is part of the exit test now that it can be blocked on its own:
    // stopping as soon as CollX and eBay are known would miss a later card that
    // only breaks Whatnot's floor.
    if (isCollxError && isEbayError && isWhatnotError) break
  }

  return {
    collx: isCollxError,
    ebay: isEbayError,
    whatnot: isWhatnotError,
    ebayTemplate: isEbayTemplateError,
    shopify: isShopifyError,
    dateScheduledTemplateError: isDateScheduledTemplateError,
    cardsWithoutCategoryBasedOnTemplate,
    cardsHasCategoryNotEqualToTemplate,
    zeroQuantityAll,
    zeroQuantityPartial,
    skuInvalid,
  }
}

export const getViewGraderWebsiteLookupLink = (grader, certNo) => {
  return `${VIEW_GRADER_WEBSITE_LOOKUP_LINKS[grader]}${certNo}`
}

 // Function to check if a cell contains a custom component that needs manual focus handling
 export const  isCustomComponentCell = (cellPosition, params) => {
  const column = cellPosition.column
  const rowNode = params.api.getDisplayedRowAtIndex(cellPosition.rowIndex)
  
  // Check based on column ID, field, or other properties identifying custom components
  return column.getColId() === 'subset' || column.getColId() === 'attributes'  // Update this ID as needed
}

// Function to manually focus the custom component in the cell
export const focusCustomComponent = (cellPosition, params) => {
  const cell = params.api.getCellRendererInstances({
    rowNodes: [params.api.getDisplayedRowAtIndex(cellPosition.rowIndex)],
    columns: [cellPosition.column],
  })[0]


  if (cell && cellPosition.column.colId === 'subset') {
    const element = cell.params?.eParentOfValue
    const inputElements = element.getElementsByTagName('input')
    const input = inputElements && inputElements[0]
    
    if (element && (typeof element.focus === 'function' || typeof input.focus === 'function')) {
      input ? input.focus() : element.focus() // Set focus to the custom component's element
    }

    if (element && (typeof element.click === 'function' || typeof input.click === 'function')) {
      input ? input.click() : element.click() // Set focus to the custom component's element

      if (!input) {
        const clickableElements = element.getElementsByTagName('section')
        const clickableEl = clickableElements[1]

        clickableEl.click()
      }
      
    } else {
      // If `click()` isn't directly available, use a custom click event
      const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true })
      element.dispatchEvent(clickEvent)
    }
  // } else if (cell && cellPosition.column.colId === 'attributes') {
  //   const element = cell.params?.eParentOfValue
  //   console.log('!!!!!!!!!!!! element', cell.params, element)

  //   if (element && typeof element.click === 'function') {
  //     const clickableElements = element.getElementsByTagName('section')
  //     const clickableEl = clickableElements[0]
  //     console.log('>> click', element)

  //     clickableEl.click()
      
  //   }
  // }
  }
}

export const getDisplayColumns = (key, userId) => {
  const savedColumns = localStorage.getItem(key)
  if (savedColumns) {
    const parsedColumns = JSON.parse(savedColumns)
    return parsedColumns[userId]
  }

  return []
}

export const getSelectedId = (selected) => {
  if (!selected) return null
  return selected.id || selected.collx_type_id || selected.type || null
}

export const valueSectionStyleForBulkInputs = ({selectedAttribute, valueState, fields}) => {
  let paddingLeft = 0
  if (
    !['status', 'start_price', 'category', 'sport', 'year', 'attributes', 'graded', 'grader', 'grade', 'purchase_price', 'condition'].includes(selectedAttribute?.field)
  ) {
    if (
      (fields[selectedAttribute.field]
      && valueState === 'entered')
      || selectedAttribute.field === 'title'
      || selectedAttribute.field === 'description'
    ) {
      paddingLeft = 17
    }
  }

  if (
    selectedAttribute.field === 'year'
    && fields?.year
    && valueState === 'entered'
  ) {
    paddingLeft = 8
  }

  if (
    selectedAttribute.field === 'attributes'
    && valueState
  ) {
    paddingLeft = 10
  }

  return {
    'padding-left': paddingLeft + 'px'
  }
}

export const getPreparedOptions = (options) => {
  return options.reduce((acc, option) => {
    if (!option.collx_set_id ) {
      acc.push({
        batch_id: null,
        collx_set_id: option.id,
        collx_set_name: option.name,
        include_parallels: option.lock_includes_parallels === undefined ? option.is_base : option.lock_includes_parallels,
        is_base: option.is_base,
      })
    } else {
      acc.push(option)
    }

    return acc
  }, [])
}